import { action, observable, computed, makeObservable } from 'mobx'

import { getURL, URL_TYPES } from '@voicekey/api/http'
import { CONFIGURATOR_API } from '@voicekey/api/configurator/configurator'

import { SEVERITIES } from '../messagesStore'
import { INSTANCE_STATUS } from './configuratorStore.const'
import dynamicMessages from 'locales/dynamicMessages'

const url = getURL(URL_TYPES.CONFIGURATOR)

export class Settings {
  constructor (rootStore) {
    makeObservable(this, {
      _component: observable,
      _token: observable,
      _userConf: observable,
      _currentState: observable,
      _components: observable,
      _instance: observable,
      _modifiedData: observable,
      _tempVersions: observable,
      _title: observable,
      resetSession: action,
      checkAuth: action,
      logout: action,
      login: action,
      handleInputChange: action,
      fillObject: action,
      addFields: action,
      removeFields: action,
      verObject: action,
      unsaved: action,
      getGlobalSettings: action,
      setGlobalSettings: action,
      getComponents: action,
      getComponent: action,
      setComponent: action,
      getInstance: action,
      setInstance: action,
      startComponent: action,
      restartComponent: action,
      stopComponent: action,
      servers: computed,
      updateInstanceStatus: action,
      deleteInstance: action
    })
    this.rootStore = rootStore
  }

  _token = ''

  get token () {
    return this._token
  }

  set token (token) {
    this._token = token
  }

  _userConf = ''

  get userConf () {
    return this._userConf
  }

  set userConf (name) {
    this._userConf = name
  }

  _currentState = ''

  get currentState () {
    return this._currentState
  }

  set currentState (state) {
    this._currentState = state
  }

  _components = []

  get components () {
    return this._components
  }

  set components (components) {
    this._components = components
  }

  _component = {}

  get component () {
    return this._component
  }

  set component (component) {
    this._component = component
  }

  _instance = {
    instanceData: {}
  }

  get instance () {
    return this._instance
  }

  set instance (instance) {
    this._instance = instance
  }

  _modifiedData = {
    values: {},
    versions: {}
  }

  get modifiedData () {
    return this._modifiedData
  }

  set modifiedData (data) {
    this._modifiedData = data
  }

  _tempVersions = {}

  get tempVersions () {
    return this._tempVersions
  }

  set tempVersions (versions) {
    this._tempVersions = versions
  }

  _title = ''

  get title () {
    return this._title
  }

  set title (value) {
    this._title = value
  }

  get servers () {
    const obj = {}

    obj.cname = {}

    this._components.forEach(function (component) {
      component.instances.forEach(function (inst) {
        if (!obj[inst.ip]) {
          obj[inst.ip] = {
            components: [],
            ip: inst.ip
          }
        }

        if (!(component.name in obj.cname)) {
          obj[inst.ip].components.push({
            name: component.name,
            status: inst.status,
            id: inst.id,
            ip: inst.ip,
            contextRoot: inst.context_root || ''
          })
        }
      })
    })

    delete obj.cname

    return Object.values(obj)
  }

  resetSession () {
    this.userConf = ''
    this.token = ''
    sessionStorage.removeItem('token')
    sessionStorage.removeItem('userConf')
  }

  async checkAuth (token = this.token, userConf = this.userConf) {
    try {
      const response = await CONFIGURATOR_API.checkAuth(url, token, userConf)
      return response
    } catch (error) {
      this.resetSession()
    }
  }

  async logout () {
    try {
      await CONFIGURATOR_API.logout(url, this.token, this.userConf)
    } finally {
      this.resetSession()
    }
  }

  async login (user) {
    try {
      const response = await CONFIGURATOR_API.login(url, user)
      this.userConf = response.data.name
      this.token = response.data.token
      sessionStorage.setItem('token', response.data.token)
      sessionStorage.setItem('userConf', response.data.name)
    } catch (error) {
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.authorization_fail,
        info: error
      })
      throw error
    }
  }

  sortByName (a, b) {
    const _a = a.name || a.description
    const _b = b.name || b.description
    return _a.toLowerCase().localeCompare(_b.toLowerCase())
  }

  handleInputChange (path, oldValue, newValue, type) {
    const pathArr = path.name.split('.')
    let value = newValue
    if (type === 'MAP') {
      value = newValue.reduce((acc, item) => {
        return item && item.value ? { ...acc, [item.value.key]: item.value.value } : acc
      }, {})
    }
    this.fillObject(this.modifiedData.values, pathArr, value, oldValue)
    this.verObject(pathArr[0], path.version)
  }

  fillObject (object, path, value, oldValue) {
    if (value === '') value = null

    if (path.length > 1) {
      if (!object[path[0]]) {
        object[path[0]] = {}
      }
      this.fillObject(object[path[0]], path.slice(1), value, oldValue)
      if (oldValue === value && Object.getOwnPropertyNames(object[path[0]]).length === 0) {
        delete object[path[0]]
      }
    } else {
      if (oldValue === value) {
        delete object[path[0]]
      } else {
        object[path[0]] = value
      }
    }
  }

  addFields (field) {
    if (field.value) {
      field.value.push(field.fields)
    } else {
      field.value = [field.fields]
    }
    return field
  }

  removeFields (field, index, path) {
    const pathName = path ? path.name + '.' : ''
    field.value[index] = null
    this.handleInputChange({
      name: pathName + field.name + '.' + index,
      version: path ? path.version : field.version
    }, field.value, null, field.type)
    return field
  }

  verObject (key, version) {
    if (this._modifiedData.values[key] && version) {
      this._modifiedData.versions[key] = this._tempVersions[key] || version
    } else {
      delete this._modifiedData.versions[key]
    }
  }

  unsaved (error) {
    if (!error.status) {
      return
    }

    const unsaved = error.data.unsaved
    const saved = error.data.saved

    for (const param in saved) {
      this._tempVersions[param] = saved[param]
      delete this._modifiedData.versions[param]
      delete this._modifiedData.values[param]
    }

    unsaved.map(un => {
      switch (un.reason) {
        case 'VERSION_MISMATCH':
        case 'NO_DESCRIPTOR':
        case 'NO_SPACE_LEFT_ON_DEVICE':
        case 'INTERNAL_SERVER_ERROR':
        case 'INIVALID_STRUCTURE':
          // marked field error about
          break
        default:
      }
    })
  }

  reset () {
    this.tempVersions = {}
    this._modifiedData = {
      values: {},
      versions: {}
    }
  }

  async getGlobalSettings () {
    try {
      const response = await CONFIGURATOR_API.getGlobalSettings(url, this.token)
      this.component = {
        name: 'common',
        data: response.data.sort(this.sortByName)
      }
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async setGlobalSettings () {
    try {
      const response = await CONFIGURATOR_API.setGlobalSettings(url, this.token, this.modifiedData)
      this.component.data = response.data.sort(this.sortByName)
      this.reset()
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.settings_saved
      }, SEVERITIES.SUCCESS)
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async getComponents () {
    try {
      const response = await CONFIGURATOR_API.getComponents(url, this.token)
      this.components = response.data.sort(this.sortByName)
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async getComponent (component) {
    try {
      const response = await CONFIGURATOR_API.getComponent(url, this.token, component)
      this.component = {
        name: component,
        data: response.data.sort(this.sortByName)
      }
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async setComponent () {
    try {
      const response = await CONFIGURATOR_API.setComponent(url, this.token, this.component.name, this.modifiedData)
      this.component.data = response.data.sort(this.sortByName)
      this.reset()
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.settings_saved
      }, SEVERITIES.SUCCESS)
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async getInstance (componentName, instanceId) {
    try {
      const response = await CONFIGURATOR_API.getInstance(url, this.token, componentName, instanceId)
      const openedComponent = this.components.find(component => component.name === componentName)
      const openedInstance = openedComponent.instances.find(instance => instance.id === instanceId)
      this.instance = {
        ...openedInstance,
        name: instanceId,
        data: response.data.sort(this.sortByName)
      }
      this.component = {
        name: componentName
      }
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async setInstance () {
    try {
      const response = await CONFIGURATOR_API.setInstance(url, this.token, this.component.name, this.instance.name, this.modifiedData)
      this.instance.data = response.data.sort(this.sortByName)
      this.reset()
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.settings_saved
      }, SEVERITIES.SUCCESS)
    } catch (error) {
      this.rootStore.messagesStore.setMessage(error)
    }
  }

  async startComponent () {
    try {
      await CONFIGURATOR_API.startComponent(url, this.token, this.component.name, this.instance.name, { host: this.instance.ip })
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.component_started
      }, SEVERITIES.SUCCESS)

      this.updateInstanceStatus(INSTANCE_STATUS.OK)
    } catch (e) {
      this.rootStore.messagesStore.setMessage(e)
    }
  }

  async restartComponent () {
    try {
      await CONFIGURATOR_API.restartComponent(url, this.token, this.component.name, this.instance.name, { host: this.instance.ip })
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.component_restarted
      }, SEVERITIES.SUCCESS)

      this.updateInstanceStatus(INSTANCE_STATUS.CONFIGURATION_UPDATE)
    } catch (e) {
      this.rootStore.messagesStore.setMessage(e)
    }
  }

  async stopComponent () {
    try {
      await CONFIGURATOR_API.stopComponent(url, this.token, this.component.name, this.instance.name, { host: this.instance.ip })
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.component_stopped
      }, SEVERITIES.SUCCESS)

      this.updateInstanceStatus(INSTANCE_STATUS.OFF)
    } catch (e) {
      this.rootStore.messagesStore.setMessage(e)
    }
  }

  async deleteInstance () {
    try {
      await CONFIGURATOR_API.deleteInstance(url, this.token, this.component.name, this.instance.name)
      this.rootStore.messagesStore.setMessage({
        ...dynamicMessages.component_deleted
      }, SEVERITIES.SUCCESS)

      const components = this.components.map(component => {
        if (!component.instances) {
          return component
        }

        if (component.name !== this.component.name) {
          return component
        }

        const instances = component.instances.filter(instance => instance.id !== this.instance.name)

        return {
          ...component,
          instances
        }
      })

      this.components = components

      const isSuccess = true

      return isSuccess
    } catch (e) {
      this.rootStore.messagesStore.setMessage(e)

      const isSuccess = false

      return isSuccess
    }
  }

  updateInstanceStatus = (status) => {
    const components = this.components.map(component => {
      if (!component.instances) {
        return component
      }

      if (component.name !== this.component.name) {
        return component
      }

      const instances = component.instances.map(instance => instance.id === this.instance.name ? { ...instance, status } : instance)

      return {
        ...component,
        instances
      }
    })

    this.components = components
    this.instance.status = status
  }
}
